import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TackLogo } from "@/components/TackLogo";
import { SiteFooter } from "@/components/SiteFooter";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Star, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useTrialCheckoutConfirmation } from "@/hooks/use-trial-checkout-confirmation";
import { useTrialDays } from "@/hooks/use-trial-days";
import { useLocalizedPrices } from "@/hooks/use-localized-prices";
import { monthlyEquivalent } from "@/lib/stripe-prices";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { isCheckoutEnabled, WAITLIST_MAILTO } from "@/lib/checkout-config";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Tack. Priser för kundlojalitet" },
      {
        name: "description",
        content:
          "Enkla månads- och årsplaner för Tack, kundlojalitet för salonger, caféer och butiker. 30 dagar gratis på Solo. Priser exkl. moms.",
      },
      { property: "og:title", content: "Tack. Priser för kundlojalitet" },
      {
        property: "og:description",
        content:
          "Planer för salonger, caféer och butiker. 30 dagar gratis på Solo, priser i SEK exkl. moms.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tack.company/pricing" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Tack. Priser" },
      {
        name: "twitter:description",
        content:
          "Planer för salonger, caféer och butiker. 30 dagar gratis på Solo.",
      },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Tack",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://tack.company/pricing",
          offers: [
            { name: "Solo (månad)", price: "199", priceCurrency: "SEK" },
            { name: "Solo (år)", price: "1990", priceCurrency: "SEK" },
            { name: "Standard (månad)", price: "499", priceCurrency: "SEK" },
            { name: "Standard (år)", price: "4990", priceCurrency: "SEK" },
            { name: "Growth (månad)", price: "1199", priceCurrency: "SEK" },
            { name: "Growth (år)", price: "11988", priceCurrency: "SEK" },
          ].map((o) => ({ "@type": "Offer", ...o, url: "https://tack.company/pricing" })),
        }),
      },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  const { t } = useTranslation();
  const { trialDays, firstChargeDay } = useTrialDays();
  const { user, isAuthenticated } = useAuth();
  const checkoutOn = isCheckoutEnabled();
  const { beginCheckout, confirmationDialog, checkoutLoading } =
    useTrialCheckoutConfirmation({ userEmail: user?.email, userId: user?.id });
  const [isYearly, setIsYearly] = useState(true);
  const prices = useLocalizedPrices([
    "tack_starter_monthly",
    "tack_starter_yearly",
    "tack_standard_monthly",
    "tack_standard_yearly",
    "tack_growth_monthly",
    "tack_growth_yearly",
    "tack_growth_extra_monthly",
    "tack_growth_extra_yearly",
    "tack_starter_overage_100",
    "tack_standard_overage_100",
    "tack_growth_overage_100",
  ]);

  const priceLabel = (id: string, fallback: string) =>
    prices?.[id]?.formatted ?? fallback;

  const handleCheckout = (priceId: string, planName: string) => {
    if (!isAuthenticated) return;
    beginCheckout({
      priceId,
      planName,
      cadence: isYearly ? "yearly" : "monthly",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <PaymentTestModeBanner />

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" aria-label="Tack home" className="hover:opacity-80 transition-opacity">
            <TackLogo size="md" showSlogan />
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="dark" />
            <Link to="/login">
              <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary/10 hover:text-primary">
                {t("common.login")}
              </Button>
            </Link>
            <Link to="/signup">
              <Button size="sm">{t("common.getStarted")}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-16 pb-8 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-tack-warm text-sm font-medium text-primary px-4 py-1.5 rounded-full mb-6">
            <Star size={14} fill="currentColor" />
            {t("landing.welcomeOffer.eyebrow")}
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight leading-tight">
            {t("landing.pricingTitle")}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            {t("landing.pricingSubtitle")}
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Welcome offer banner (moved up) */}
          <div className="mb-8 rounded-2xl bg-gradient-to-br from-primary/15 via-tack-warm to-primary/10 border border-primary/25 px-6 py-6 md:px-8 md:py-7 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center">
                <Star size={26} className="text-primary" fill="currentColor" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary mb-1">
                  {t("landing.welcomeOffer.eyebrow")}
                </div>
                <h2 className="text-lg md:text-xl font-display font-bold text-foreground leading-snug">
                  {t("landing.welcomeOffer.headline", { days: trialDays })}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("landing.welcomeOffer.subline")}
                </p>
                <p className="mt-2 text-xs text-muted-foreground/80">
                  {t("landing.welcomeOffer.footnote")}
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimers */}
          <p className="text-xs text-muted-foreground text-center mb-2 max-w-2xl mx-auto">
            {t("landing.availabilityNote")}
          </p>
          <p className="text-xs text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            {t("landing.taxDisclaimer")}
          </p>

          {/* Billing toggle */}
          <div className="relative flex flex-wrap items-center justify-center gap-3 mb-8">
            <span className={`shrink-0 text-sm font-medium ${!isYearly ? "text-foreground" : "text-muted-foreground"}`}>
              {t("landing.monthly")}
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              aria-label="Toggle billing cadence"
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${isYearly ? "bg-primary" : "bg-muted"}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${isYearly ? "translate-x-6" : "translate-x-1"}`} />
            </button>
            <span className={`shrink-0 text-sm font-medium ${isYearly ? "text-foreground" : "text-muted-foreground"}`}>
              {t("landing.yearly")}
            </span>
            <Badge
              className={`text-xs bg-tack-green/10 text-tack-green border-tack-green/20 whitespace-nowrap transition-opacity ${isYearly ? "opacity-100 max-sm:order-last max-sm:basis-full max-sm:flex max-sm:justify-center" : "opacity-0 pointer-events-none max-sm:hidden"}`}
            >
              {t("landing.saveWithAnnual")}
            </Badge>
          </div>


          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {/* Starter */}
            <Card className="border-border/50 flex flex-col">
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display font-bold text-xl">{t("landing.tiers.starter")}</h3>
                  <Badge className="bg-tack-green/10 text-tack-green border-tack-green/20 text-xs">
                    {t("landing.trialBadgeSolo", { days: trialDays })}
                  </Badge>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-display font-bold">
                    {isYearly
                      ? (monthlyEquivalent(prices?.["tack_starter_yearly"]) ?? "166 kr")
                      : priceLabel("tack_starter_monthly", "199 kr")}
                  </span>
                  <span className="text-muted-foreground text-sm">{t("landing.perMonth")}</span>
                </div>
                {isYearly && (
                  <p className="text-xs text-muted-foreground">
                    {t("landing.billedYearly", { price: priceLabel("tack_starter_yearly", "1 990 kr") })}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mb-3">{t("landing.exclVat")}</p>
                <ul className="space-y-2 flex-1">
                  {[
                    t("landing.features_starter_lead"),
                    t("landing.features_starter_qr"),
                    t("landing.features_starter_sms", { price: priceLabel("tack_starter_overage_100", "150 kr") }),
                    t("landing.features_starter_send"),
                    t("landing.features_starter_qrs"),
                    t("landing.features_starter_dash"),
                    t("landing.features_starter_burn"),
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check size={14} className="text-tack-green shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isAuthenticated ? (
                  checkoutOn ? (
                    <Button variant="outline" className="w-full mt-6 whitespace-normal h-auto min-h-10 py-2 leading-tight" onClick={() => handleCheckout(isYearly ? "tack_starter_yearly" : "tack_starter_monthly", t("landing.tiers.starter"))} disabled={checkoutLoading}>
                      {checkoutLoading ? t("common.loading") : t("landing.subscribeTo", { plan: t("landing.tiers.starter") })}
                    </Button>
                  ) : (
                    <a href={WAITLIST_MAILTO} className="block mt-6">
                      <Button variant="outline" className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("landing.waitlistCta")}</Button>
                    </a>
                  )
                ) : (
                  <Link to="/signup" className="block mt-6">
                    <Button variant="outline" className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("landing.signupCta")}</Button>
                  </Link>
                )}
                <p className="mt-2 text-xs text-muted-foreground">{t("landing.trialNoteSolo", { days: trialDays, firstChargeDay })}</p>
              </CardContent>
            </Card>

            {/* Standard — Popular */}
            <Card className="border-primary shadow-md relative flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                {t("landing.popular")}
              </div>
              <CardContent className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-bold text-xl mb-1">{t("landing.tiers.standard")}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-display font-bold">
                    {isYearly
                      ? (monthlyEquivalent(prices?.["tack_standard_yearly"]) ?? "416 kr")
                      : priceLabel("tack_standard_monthly", "499 kr")}
                  </span>
                  <span className="text-muted-foreground text-sm">{t("landing.perMonth")}</span>
                </div>
                {isYearly && (
                  <p className="text-xs text-muted-foreground">
                    {t("landing.billedYearly", { price: priceLabel("tack_standard_yearly", "4 990 kr") })}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mb-3">{t("landing.exclVat")}</p>
                <ul className="space-y-2 flex-1">
                  {[
                    t("landing.features_standard_lead"),
                    t("landing.features_standard_1"),
                    t("landing.features_standard_sms", { price: priceLabel("tack_standard_overage_100", "120 kr") }),
                    t("landing.features_standard_2"),
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check size={14} className="text-tack-green shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isAuthenticated ? (
                  checkoutOn ? (
                    <Button className="w-full mt-6 whitespace-normal h-auto min-h-10 py-2 leading-tight" onClick={() => handleCheckout(isYearly ? "tack_standard_yearly" : "tack_standard_monthly", t("landing.tiers.standard"))} disabled={checkoutLoading}>
                      {checkoutLoading ? t("common.loading") : t("landing.subscribeTo", { plan: t("landing.tiers.standard") })}
                    </Button>
                  ) : (
                    <a href={WAITLIST_MAILTO} className="block mt-6">
                      <Button className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("landing.waitlistCta")}</Button>
                    </a>
                  )
                ) : (
                  <Link to="/signup" className="block mt-6">
                    <Button className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("landing.signupCta")}</Button>
                  </Link>
                )}
                <p className="mt-2 text-xs text-muted-foreground">{t("landing.noTrialNote")}</p>
              </CardContent>
            </Card>

            {/* Growth */}
            <Card className="border-border/50 flex flex-col">
              <CardContent className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-bold text-xl mb-1">{t("landing.tiers.growth")}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-display font-bold">
                    {isYearly
                      ? (monthlyEquivalent(prices?.["tack_growth_yearly"]) ?? "999 kr")
                      : priceLabel("tack_growth_monthly", "1 199 kr")}
                  </span>
                  <span className="text-muted-foreground text-sm">{t("landing.perMonth")}</span>
                </div>
                {isYearly && (
                  <p className="text-xs text-muted-foreground">
                    {t("landing.billedYearly", { price: priceLabel("tack_growth_yearly", "11 988 kr") })}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mb-3">{t("landing.exclVat")}</p>
                <ul className="space-y-2 flex-1">
                  {[
                    t("landing.features_growth_lead"),
                    t("landing.features_growth_1"),
                    t("landing.features_growth_pool"),
                    t("landing.features_growth_sms", { price: priceLabel("tack_growth_overage_100", "90 kr") }),
                    t("landing.features_growth_extra", {
                      monthly: priceLabel("tack_growth_extra_monthly", "999 kr"),
                      yearly: priceLabel("tack_growth_extra_yearly", "9 990 kr"),
                    }),
                    t("landing.features_growth_3"),
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check size={14} className="text-tack-green shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isAuthenticated ? (
                  checkoutOn ? (
                    <Button variant="outline" className="w-full mt-6 whitespace-normal h-auto min-h-10 py-2 leading-tight" onClick={() => handleCheckout(isYearly ? "tack_growth_yearly" : "tack_growth_monthly", t("landing.tiers.growth"))} disabled={checkoutLoading}>
                      {checkoutLoading ? t("common.loading") : t("landing.subscribeTo", { plan: t("landing.tiers.growth") })}
                    </Button>
                  ) : (
                    <a href={WAITLIST_MAILTO} className="block mt-6">
                      <Button variant="outline" className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("landing.waitlistCta")}</Button>
                    </a>
                  )
                ) : (
                  <Link to="/signup" className="block mt-6">
                    <Button variant="outline" className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("landing.signupCta")}</Button>
                  </Link>
                )}
                <p className="mt-2 text-xs text-muted-foreground">{t("landing.noTrialNote")}</p>
              </CardContent>
            </Card>

            {/* Enterprise */}
            <Card className="border-border/50 flex flex-col">
              <CardContent className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-bold text-xl mb-1">{t("landing.tiers.enterprise")}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-display font-bold">{t("landing.custom")}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{t("landing.talkToUs")}</p>
                <ul className="space-y-2 flex-1">
                  {[
                    t("landing.features_enterprise_lead"),
                    t("landing.features_enterprise_1"),
                    t("landing.features_enterprise_2"),
                    t("landing.features_enterprise_4"),
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check size={14} className="text-tack-green shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="mailto:hello@tack.company?subject=Enterprise%20Plan%20Inquiry" className="block mt-6">
                  <Button variant="outline" className="w-full whitespace-normal h-auto min-h-10 py-2 leading-tight">{t("landing.contactUs")}</Button>
                </a>
              </CardContent>
            </Card>
          </div>



          {/* Included-message top-ups note */}
          <div className="mt-10 max-w-2xl mx-auto rounded-xl border border-border/60 bg-card px-5 py-4 text-sm text-muted-foreground text-center">
            {t("landing.topupNote")}
            <p className="mt-2 text-xs">{t("landing.welcomeCreditsAll")}</p>
          </div>


          {/* CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="gap-2 px-8">
                {t("common.startFreeTrial")}<ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/faq">
              <Button variant="outline" size="lg">
                {t("footer.faq")}
              </Button>
            </Link>
          </div>

          {/* Billing & seller disclosure */}
          <p className="mt-10 text-xs text-muted-foreground text-center max-w-2xl mx-auto">
            {t("landing.morNote")}
          </p>
        </div>
      </section>

      <SiteFooter variant="marketing" />
      {confirmationDialog}
    </div>
  );
}
